import type { LumpsumPlanStatus } from '../../../api/common.types'
import { isStrategyInProgress, shouldShowStrategyShell } from '../shared/planStatus'
import { isPlanDropDelayed, usePlanCountdown } from '../shared/usePlanCountdown'
import { ALLOCATION_FEATURE_ROWS } from '../shared/allocationFeatureRows'
import {
  TimerPanelCountdownSection,
  TimerPanelFeatureRows,
  TimerPanelFooterCopy,
  TimerPanelFooterHeading,
  TimerPanelShell,
  TimerPanelStrategySection
} from '../shared/TimerPanelShell'
import { PlansScreen } from '../../../../analytics/plansEvents'
import { getPlansFlowTheme, PlansThemeFlow } from '../../header/common/riskProfileHeaderTheme'
import { primeTimerCountdownChrome } from '../shared/timerCountdownChrome'

const FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.ALLOCATION)

type AllocationTimerPanelProps = {
  planStatus?: LumpsumPlanStatus | null
  timeToPlanDrop?: number | null
  isSubscriptionEligible: boolean
  strategyCta?: unknown
  refetchPlan: () => void
}

/** Strategy / timer / delayed shell before PLAN_ROLLED_OUT. */
export function AllocationTimerPanel({
  planStatus,
  timeToPlanDrop,
  isSubscriptionEligible,
  strategyCta,
  refetchPlan
}: AllocationTimerPanelProps) {
  const showStrategy = shouldShowStrategyShell(planStatus)
  const isDelayed = !showStrategy && isPlanDropDelayed(timeToPlanDrop)
  const countdownParts = usePlanCountdown(showStrategy || isDelayed ? 0 : timeToPlanDrop)

  return (
    <TimerPanelShell
      background={FLOW_THEME.chromeBackground}
      title="Allocation Plan"
      description="Find how much you should invest in each category, based on your life stage and risk appetite."
      hero={
        showStrategy ? (
          <TimerPanelStrategySection
            promptClassName={FLOW_THEME.primaryDarkTextClassName}
            variant={isStrategyInProgress(planStatus) ? 'complete' : 'find'}
            strategyCta={strategyCta}
            refetchPlan={refetchPlan}
            analyticsScreen={PlansScreen.LumpsumAllocationTimer}
          />
        ) : (
          <TimerPanelCountdownSection
            {...primeTimerCountdownChrome(FLOW_THEME, {
              readyIn: 'Portfolio under review, your plan will be ready in',
              delayTitle: 'Your allocation plan is delayed',
              delayBody:
                "We need extra time to prepare your Allocation plan. We'll update you as soon as it's ready.",
              unlockPrompt: "Unlock your allocation plan once it's ready"
            })}
            countdownParts={countdownParts}
            isDelayed={isDelayed}
            isSubscriptionEligible={isSubscriptionEligible}
            unlockButtonLabel="Unlock with Prime"
            landingPage="prime"
            analyticsScreen={PlansScreen.LumpsumAllocationTimer}
          />
        )
      }
      footer={
        <>
          <TimerPanelFooterHeading>Get overall portfolio guidance from experts</TimerPanelFooterHeading>
          <TimerPanelFooterCopy>
            Our experts review your portfolio to identify any optimisations to keep it aligned with your
            strategy. Here&apos;s what we focus on:
          </TimerPanelFooterCopy>
          <TimerPanelFeatureRows rows={ALLOCATION_FEATURE_ROWS} iconColor={FLOW_THEME.primary} />
        </>
      }
    />
  )
}
